import type { PlanCode, SourceStatus } from './enums.ts';

/**
 * Public-playground defaults (design spec's "Lifetime, limits and what the visitor is
 * told") — the ONE place these numbers live. Task 3 (`demo_crawl`/`demo_reap`) consumes
 * `maxPages`/`reapIntervalMinutes`/`crawlDeadlineMs`/`reapBatchSize`; Task 4 consumes
 * `lifetimeMinutes`/`maxMessages`/`demosPerIpPerHour`/`maxConcurrentDemos` for the public HTTP
 * endpoints. Never restate any of these as a literal anywhere else — same discipline as
 * `PLAN_LIMITS` beside it.
 */
export interface PlaygroundLimits {
  /** How long a demo lives before `demo_reap` tears it down. */
  lifetimeMinutes: number;
  /** Hard page cap for a `demo_crawl` run — small enough that one URL can't drain the AI
   * Search instance quota, large enough to answer real questions about a site. */
  maxPages: number;
  /** Chat messages allowed per demo before Task 4's routes refuse the next one. */
  maxMessages: number;
  /** Demos a single IP may create per rolling hour (design spec's "Abuse surface").
   * Counts only demos that delivered value — `error`/`needs_js` outcomes are excluded, so a
   * visitor whose site blocked the crawler can immediately try another URL (launch-day
   * finding, 2026-08-20: three failed sites locked a real visitor out for an hour having
   * received nothing). Caveat: `markExpired` erases the failure status after the 30-minute
   * lifetime, so failures older than that re-enter this count until they age out of the
   * window — acceptable; the acute case is "failed just now, retry now". */
  demosPerIpPerHour: number;
  /** Backstop: TOTAL creation attempts per IP per rolling hour regardless of outcome, so
   * excluding failures above can't be abused to probe domains without limit. */
  attemptsPerIpPerHour: number;
  /** How often `demo_reap` re-enqueues itself. */
  reapIntervalMinutes: number;
  /**
   * Whole-crawl wall-clock ceiling for `demo_crawl` (Task 3, round-1 review finding I1) — an
   * attacker-controlled target can otherwise hold the crawl up for ~12.5 minutes (60s
   * robots/sitemap prologue + 30 retry attempts × up to ~25s each, the per-attempt figure
   * itself driven by the TARGET's own `robots.txt` `Crawl-delay`), which does two bad things:
   * the slow lane is `batch: 1` and ticks serially, so that one demo delays every paying
   * customer's `crawl_source` behind it; and 12.5 min exceeds `poller.ts`'s `REAP_STALE_MS`
   * (10 min), so the fast lane's reaper resets the still-`crawling` job to `pending` and a
   * re-claim re-runs the whole thing. 3 minutes is comfortably above what 10 STATIC pages
   * legitimately need (no rendered-mode wait, no thousands-of-pages BFS) and comfortably below
   * `REAP_STALE_MS`, mirroring how `crawl_source` pins `RENDERED_MODE_CRAWL_DEADLINE_MS` (6
   * min) under the same reap window for the same reason.
   */
  crawlDeadlineMs: number;
  /**
   * `demo_reap`'s per-pass row cap (Task 3, round-1 review finding I5) — the fast lane runs
   * claimed jobs serially and skips ticks while one is in flight, so an uncapped pass (the
   * repo default `take = 500`) at ~2 CF/DB round trips per row can hold the lane for 2+
   * minutes, during which `provision_instance` — interactive bot creation, a paying customer
   * waiting — does not run. A Product Hunt burst plus a 30-minute lifetime makes a
   * several-hundred-row backlog ordinary, not exceptional. 50 rows/pass at
   * `reapIntervalMinutes: 5` is 600 rows/hour of drain capacity — well above what that burst
   * produces — while keeping any one pass short enough not to starve the fast lane.
   */
  reapBatchSize: number;
  /**
   * Global concurrent-demo ceiling (design spec's "Abuse surface" — `DemoBot.ipHash`'s own
   * doc comment already promised this exists; Task 3 round-1 review added the constant and
   * the repo method, `demoBots.countLive`, that reads against it). Bounds how many demos can
   * occupy AI Search instance budget at once, independent of the per-IP cap (which a burst
   * across many IPs doesn't touch). 200 is well clear of the 5,000-instance-per-account cap on
   * Workers Paid, and generous for a single launch burst — Task 4 reads this constant rather
   * than inventing a number in a route.
   */
  maxConcurrentDemos: number;
  /**
   * Round-1 review finding I5 — how long an `expired` `DemoBot` row survives after its own
   * `expiresAt` before `demo_reap`'s retention sweep (`deleteRetired`, `@repo/db`) deletes it
   * outright. Before this, the reaper only ever set `status: 'expired'` and never removed the
   * row, so the table grew without bound — and with C1 (the per-IP cap being decorative
   * against a spoofed `X-Forwarded-For`) unfixed, an attacker could inflate it at will, making
   * `countLive`'s scan degrade under exactly the load it exists to survive. 60 minutes is
   * generous for any debugging/support window (a demo's own lifetime is 30) while still
   * bounding growth aggressively — nothing in a demo is meant to outlive it by much.
   */
  retentionMinutes: number;
  /**
   * Round-2 review R3 minor — the rolling window `demosPerIpPerHour` is measured over, in
   * milliseconds. Was the one playground number living in `routes/demo.ts` instead of here;
   * moved so it's derived from the SAME place as the cap it belongs to rather than restated
   * near the call site. `60 * 60 * 1000` — this is what `demosPerIpPerHour`'s own name
   * declares the unit to be, not an independent number.
   */
  ipCapWindowMs: number;
  /**
   * Playground spend cap (docs/superpowers/specs/2026-08-14-playground-spend-cap-design.md) —
   * the safe default that always exists in code for total playground messages/day, across
   * every demo combined (distinct from `maxMessages`, which caps a SINGLE demo). At ≤2 LLM
   * calls/message on the default chat model, this is the "tens of dollars" worst case the PH
   * launch checklist §1 already computes. `apps/api/src/lib/env.ts`'s
   * `PLAYGROUND_DAILY_MESSAGE_CAP` can override this per environment WITHOUT a deploy;
   * absent/blank there always falls back to this constant — absence must never mean unlimited
   * (the unsafe direction). The hard chat-ceiling is always DERIVED from this value in INTEGER
   * arithmetic — `Math.ceil((dailyMessageCap * 11) / 10)`, `dailyMessageCeiling` in
   * `apps/api/src/routes/demo.ts` — never a second constant, and never the float form
   * `dailyMessageCap * 1.1`: measured 2026-08-14, that form ceils one too high for 925 of the
   * first 20,000 integers (`100 * 1.1 === 110.00000000000001`), and this cap is env-reachable
   * (`PLAYGROUND_DAILY_MESSAGE_CAP`), so "the code default happens to be safe" was never a
   * guarantee for every value Johnny might configure.
   */
  dailyMessageCap: number;
}

export const PLAYGROUND_LIMITS: PlaygroundLimits = {
  lifetimeMinutes: 30,
  maxPages: 10,
  maxMessages: 10,
  demosPerIpPerHour: 3,
  attemptsPerIpPerHour: 10,
  reapIntervalMinutes: 5,
  crawlDeadlineMs: 180_000,
  reapBatchSize: 50,
  maxConcurrentDemos: 200,
  retentionMinutes: 60,
  ipCapWindowMs: 60 * 60 * 1000,
  dailyMessageCap: 10_000,
};

/** Full plan facts (PRD pricing table) — `name`/`priceUsd`/`maxMessages` back the Billing
 * screen (spec D7/M5 task-6); `maxPages`/`maxBots` are the pre-existing enforcement caps
 * (`routes/bots.ts`, `jobs/handlers/crawl-source.ts`). There is no `plans` DB table — this
 * static map IS the plan catalog, same as before this task, just with more facts on it. */
export interface PlanFacts {
  name: string;
  priceUsd: number;
  maxPages: number;
  maxBots: number;
  maxMessages: number;
  /** Whether this plan REQUIRES the "Powered by Embent" footer badge — the plan fact, not the
   * rendered outcome (badge opt-in spec, 2026-08-11). Free `true`; every paid plan and internal
   * `false`, meaning the badge is optional there, not absent — the owner's per-bot
   * `BotSettings.showBadge` decides for those plans. The two combine server-side, in exactly
   * one place: `resolveShowsBadge` below. Lives here, beside the numeric caps, because this
   * fact has TWO consumers that must never disagree: the pricing page's per-plan feature list
   * (`apps/web/app/routes/pricing.tsx`) and the dashboard's Billing screen
   * (`apps/dashboard/src/pages/Billing.tsx`). They did disagree once already: the pricing page
   * promised "No badge" on Starter/Growth from launch while the widget rendered the badge
   * unconditionally on every plan, so paying customers kept our branding (found 2026-08-08 in
   * the competitor/pricing review — see `docs/design/000-market-analysis.md` §5.3). Duplication
   * was the root cause; one constant is the fix. Removing branding is the single most resented
   * upsell in this market (Chatbase charges $1,188/yr for it on every tier), so a paid plan can
   * always turn it back on if it wants — see `resolveShowsBadge`. */
  badgeRequired: boolean;
  /** Seats per workspace, counting the owner — spec 2026-08-20-workspace-members-design.md */
  members: number;
}

export const PLAN_LIMITS: Record<PlanCode, PlanFacts> = {
  free: {
    name: 'Free',
    priceUsd: 0,
    maxPages: 50,
    maxBots: 1,
    maxMessages: 100,
    badgeRequired: true,
    members: 1,
  },
  starter: {
    name: 'Starter',
    priceUsd: 29,
    maxPages: 2000,
    maxBots: 1,
    maxMessages: 3000,
    badgeRequired: false,
    members: 3,
  },
  growth: {
    name: 'Growth',
    priceUsd: 79,
    maxPages: 10000,
    maxBots: 5,
    maxMessages: 15000,
    badgeRequired: false,
    members: 10,
  },
};

/** THE single definition of the badge rule (badge opt-in spec, 2026-08-11) — a required badge
 * (Free) ignores the owner's setting; an optional badge (every paid plan, internal) follows it.
 * Pure and DB-free so it's testable without a database. The one server-side caller is
 * `apps/api/src/app.ts`'s `PublicHelpers.showsBadge`, which decides what the widget actually
 * renders. `apps/dashboard/src/pages/BotAppearance.tsx` calls this SAME function again,
 * client-side, twice — to drive the live badge-preview toggle ahead of a save round-trip — and
 * must keep mirroring the server through this one helper rather than re-deriving
 * `badgeRequired || showBadge` by hand, which is exactly how the two would drift apart. Note
 * this is `||`, not `&&`: `badgeRequired` always wins toward SHOWING the badge, never toward
 * hiding it. */
export function resolveShowsBadge(input: { badgeRequired: boolean; showBadge: boolean }): boolean {
  return input.badgeRequired || input.showBadge;
}

/** Admin v2 / internal workspaces (spec
 * 2026-08-06-admin-v2-internal-workspaces-design.md §A) — the effective `PlanFacts` for a
 * `Workspace.internal = true` workspace: every numeric cap is unlimited. `Infinity` (not a
 * sentinel like `-1`) so every existing comparison against `PLAN_LIMITS[plan].maxX` (`>=`,
 * `* 1.1`, ...) just keeps working unmodified — an internal workspace can never trip a cap
 * built out of ordinary numeric comparisons. Kept as a `PlanFacts`-shaped value (same type as
 * `PLAN_LIMITS`'s entries, "per existing DTO shape") rather than widening any cap field to
 * `number | null`, specifically so `apps/dashboard` (out of scope for this task) keeps
 * compiling unmodified against `BillingSummaryDto`/`AdminWorkspaceRow`. */
export const UNLIMITED_PLAN_FACTS: PlanFacts = {
  name: 'Internal',
  priceUsd: 0,
  maxPages: Infinity,
  maxBots: Infinity,
  maxMessages: Infinity,
  // An internal workspace outranks every paid plan, so it gets the paid plans' treatment here
  // too — our own dogfood bots on embent.com must not be REQUIRED to advertise "Powered by
  // Embent" at the bottom of a widget that is already on our own site. (An internal bot can
  // still opt in via `BotSettings.showBadge`, same as any paid plan — see `resolveShowsBadge`.)
  badgeRequired: false,
  // Seat cap bypass, same as every other numeric cap here (workspace-members design's "Seats
  // are a plan fact" — internal workspaces bypass the seat check entirely).
  members: Infinity,
};

// `deleting` is reachable from anywhere (an owner can delete a source mid-crawl) and leads
// nowhere except `error` (fix round 1, source-detail-diagnostics Task 4 review, Important 3):
// the teardown job (`delete_source_docs`) removes the row on success, so the happy path never
// transitions back out at all. But if that job exhausts its attempts (e.g. a provider outage
// making the teardown call fail repeatedly), the row must not sit in `deleting` forever with
// every action on it permanently disabled and no recovery short of support — the poller's
// generic terminal-failure finalization (`markSourceErrorIfJobTerminal`, `apps/api/src/jobs/
// poller.ts`) needs `deleting → error` to be legal so it can mark the row visibly failed
// instead of hitting the illegal-transition guard and giving up silently. From `error`, the
// owner can delete again — and Resync/PATCH also become available again, technically able to
// revive a source they tried to delete. That's odd but visible and owner-chosen; no special
// case exists to prevent it.
const SOURCE_STATUS_TRANSITIONS: Record<SourceStatus, readonly SourceStatus[]> = {
  pending: ['crawling', 'indexing', 'error', 'deleting'],
  crawling: ['indexing', 'error', 'deleting'],
  indexing: ['ready', 'error', 'deleting'],
  // NOT `'error'` (index-verification Task 3, round 1 added it for finding I2, round 2's
  // re-review reverted it — final whole-branch review, finding 6): `verify_indexing`'s
  // all-documents-failed roll-up never routes through `canTransition` at all —
  // `sources.markErrorIfReadyUnscoped` is a status-guarded CAS that bypasses this table by
  // design (same precedent as `annotateReadyUnscoped`/`setRenderedMode`: "this isn't a pipeline
  // transition", see that function's own doc comment), specifically because legalizing
  // `ready→error` here ALSO legalized it as reachable through every OTHER already-legal
  // `X→error` edge for a stale, superseded caller (round 1's own regression: a stale verify
  // tick could stomp a source a newer `crawl_source` had already reclaimed via
  // `setStatusUnscoped`). `poller.ts`'s `markSourceErrorIfJobTerminal` also no longer relies on
  // this table (an explicit `status === 'ready'` pre-check, plus skipping `verify_indexing`'s
  // kind entirely). With no remaining caller that traverses `ready→error` through
  // `canTransition`, keeping the edge here is pure unused legality for a future stale caller to
  // rediscover the same hole with — removed.
  ready: ['crawling', 'indexing', 'deleting'],
  error: ['crawling', 'indexing', 'deleting'],
  deleting: ['error'],
};

/** Pure guard for `DataSource.status` transitions — same-state is never allowed. */
export function canTransition(from: SourceStatus, to: SourceStatus): boolean {
  return SOURCE_STATUS_TRANSITIONS[from].includes(to);
}

/** Worst-first severity for a bot's sources (error is most severe, ready is least) — the ONE
 * definition, `Record<SourceStatus, number>` so adding a `SourceStatus` member without a
 * severity fails the build instead of silently ranking it below `ready`. Previously duplicated
 * as this exhaustive shape in `apps/api/src/routes/bots.ts` (single-bot `computeKnowledge`) AND
 * as a bare `SourceStatus[]` in `packages/db/src/repos/bots.ts` (`listWithHealth`'s N-bot
 * aggregate) — the second copy compiled fine with a status missing, which would have made
 * `worstStatus` silently `null` and painted a green "ready" chip beside a bot that actually had
 * an unrecognized-status source. Both now read this one constant. */
export const STATUS_SEVERITY: Record<SourceStatus, number> = {
  error: 5,
  pending: 4,
  crawling: 3,
  indexing: 2,
  ready: 1,
  // Least severe on purpose: a source being torn down is not a problem with the bot, and it
  // is about to stop existing. Ranking it above `ready` would repaint a healthy bot amber
  // for the few seconds a delete takes.
  deleting: 0,
};

/** `STATUS_SEVERITY`'s keys, worst-first — the shape `listWithHealth`'s `.find(...)` scan
 * wants. Derived from `STATUS_SEVERITY` (not hand-duplicated) so the two can never disagree. */
export const STATUS_SEVERITY_ORDER: SourceStatus[] = (Object.keys(STATUS_SEVERITY) as SourceStatus[]).sort(
  (a, b) => STATUS_SEVERITY[b] - STATUS_SEVERITY[a],
);

/** Stored-sample cap for a crawl run's failed URLs and off-site redirects. `failedUrls` costs
 * detail, never accuracy — the run also carries the true total (`failedCount`), so a
 * 2,000-page crawl of a broken site writes a bounded row without losing the count.
 * `offSiteRedirects` has no such total column: past this cap it is a genuine sample, and a
 * caller can't tell 50 redirects from 200 truncated to 50. */
export const FAILED_URL_SAMPLE_CAP = 50;

/** The `hint:`-prefix convention `DataSource.error` uses to mark an advisory, amber notice
 * (a successful-but-truncated crawl, a near-empty-pages nudge) apart from a hard `error`-status
 * failure — `crawl-source.ts` is the only writer (`renderedModeHint`/`renderedModeRateLimitHint`/
 * `renderedModeDeadlineHint`/`offSiteRedirectHint`), always as a template literal starting
 * exactly `hint: ` (colon, one space, then the message — never padded further).
 *
 * Final review (source-detail-diagnostics): before this, four independent call sites each
 * re-implemented this check, and two literals had crept in — `crawl-source.ts` used `'hint: '`
 * (6 chars, matching what it actually writes) while `SourceDetail.tsx`/`BotSources.tsx` used
 * `'hint:'` (5 chars) then `.slice(5).trim()`. Both stripped to the same string today only
 * because there happens to be exactly one space after the colon in every message this file
 * writes — a coincidence, not a contract, and not something a future editor of either file would
 * know to preserve. One helper, one literal. */
export const HINT_PREFIX = 'hint: ';

/** Whether `error` is one of `DataSource.error`'s advisory `hint:`-prefixed notices, as opposed
 * to a hard failure — see `HINT_PREFIX`'s own doc comment for the full convention. */
export function isHintMessage(error: string): boolean {
  return error.startsWith(HINT_PREFIX);
}

/** Strips the advisory `hint: ` prefix for display, if present; a no-op on any string that
 * doesn't have it. */
export function stripHintPrefix(error: string): string {
  return isHintMessage(error) ? error.slice(HINT_PREFIX.length) : error;
}

/** The ONE wording for "some of a source's pages failed CF indexing" (index-verification
 * design, 2026-08-14) — shared so the two independent renderers of this fact, `verify_indexing`'s
 * `partialFailureHint` (writes it, `hint`-prefixed, onto `DataSource.error`) and the dashboard's
 * `CrawlDiagnostics` panel (reads `SourceDocumentsPageDto.erroredCount`/`totalCount` and renders
 * it as a standalone line), can never hand-write two different variants of the same sentence. */
export function indexingFailureMessage(errored: number, total: number): string {
  return `${errored} of ${total} pages failed indexing`;
}

/** `verify_indexing`'s advisory hint when Cloudflare itself paused the bot's AI Search instance
 * (index-verification design, 2026-08-14; final whole-branch review, finding 4). A FIXED
 * sentence, not derived from a per-tick fact the way `indexingFailureMessage` is — so a plain
 * exported constant is the right shape, not a function: the writer (`verify_indexing`, on a
 * paused list call) and every reader that needs the EXACT string (`isVerifyOwnedHint`'s
 * equality check, below) import this one value, so they can never drift onto different wording
 * the same way `partialFailureHint` no longer hand-writes its own copy of
 * `indexingFailureMessage`'s sentence. */
export const PAUSED_INSTANCE_HINT =
  `${HINT_PREFIX}Cloudflare AI Search paused this bot's indexing instance — indexing ` +
  'verification is stopped until a human resumes it (contact support)';

const VERIFY_OWNED_PARTIAL_FAILURE_PATTERN = /^hint: \d+ of \d+ pages failed indexing$/;

/** Whether `error` is one of `verify_indexing`'s OWN advisory `hint:` messages — the
 * partial-failure "N of M pages failed indexing" shape (`indexingFailureMessage`) or the fixed
 * `PAUSED_INSTANCE_HINT` — as opposed to a hint some OTHER writer put there (`crawl_source`'s
 * near-empty/rate-limit notices, etc).
 *
 * ONE canonical definition (final whole-branch review, finding 4c), used by BOTH sides of the
 * fact: the writer's own clearing logic (`verify-indexing.ts`, deciding whether a hint it finds
 * on the source is safe to clear) and the dashboard's `isCrawlNarratedError`
 * (`apps/dashboard/src/lib/crawl-run.ts`, deciding whether a verify-owned hint should reach the
 * Source Detail header instead of being suppressed as "the crawl already said this"). Before
 * this, the paused-instance hint was invisible on Source Detail (suppressed by the header's
 * blanket `hint:` check, and never rendered by `CrawlDiagnostics` either — a paused instance
 * leaves every document `pending`, so `erroredCount` stays 0) AND never self-cleared once
 * support resumed the instance for a text/file source (no `crawl_source` ever runs for those,
 * and the old pattern-only check didn't recognize the paused shape at all). One shared
 * predicate, used everywhere a hint's OWNERSHIP is the question being asked, is what keeps
 * those two facts from drifting apart again. */
export function isVerifyOwnedHint(error: string): boolean {
  return VERIFY_OWNED_PARTIAL_FAILURE_PATTERN.test(error) || error === PAUSED_INSTANCE_HINT;
}

/** THI-99 item 2 — the crawler's own "this page came back with basically nothing" threshold
 * (`isNearEmpty`, `apps/api/src/crawler/renderers/index.ts`), and the ONE number the
 * dashboard's near-empty badge/filter has to agree with. Second consumer:
 * `listBySourcePaginated` (`@repo/db`'s `documents.ts`) approximates this same rule in SQL
 * (Postgres `btrim`, not JS `.trim()`) to flag rows and filter the source-detail documents
 * list. Never restate `200` as a literal in either place — same discipline as
 * `HINT_PREFIX`/`PLAN_LIMITS`. */
export const NEAR_EMPTY_MIN_CHARS = 200;

/** Only the "N of M pages <verb phrase>" LEAD-IN, deliberately not anchored to a full sentence
 * or to one exact verb phrase — `renderedModeHint` (`apps/api/src/jobs/handlers/crawl-source.ts`)
 * has now been reworded twice (2026-08-18 THI-99: review-first framing replaced "enable
 * rendered mode if they need JavaScript"; same day, two-tier framing added a SECOND variant,
 * "came back empty — this site likely needs rendered mode.", alongside the light-tier "looked
 * empty — review them before enabling rendered mode."), but existing `DataSource.error` rows on
 * dev/prod still carry the ORIGINAL sentence and must keep deep-linking — a pattern anchored to
 * one exact verb phrase would stop recognizing whichever variant isn't current the moment the
 * copy changes again. Capturing groups feed `parseNearEmptyHint` below. */
const NEAR_EMPTY_HINT_PATTERN = /^(\d+) of (\d+) pages (?:looked empty|came back empty)/;

export interface NearEmptyHintCounts {
  /** How many pages triggered the nudge. */
  count: number;
  /** How many pages the crawl emitted in total. */
  total: number;
}

/** Parses `{ count, total }` out of ANY near-empty hint variant `crawl_source` has ever
 * written — the original copy, and both current light/heavy tiers (see
 * `NEAR_EMPTY_HINT_PATTERN`'s own doc comment) — off the STRIPPED hint text (`stripHintPrefix`,
 * not the raw `hint: `-prefixed error). Returns `null` for anything else, including a
 * `hint:`-prefixed message that ISN'T this nudge (rate-limit/deadline truncation, off-site
 * redirects, `verify_indexing`'s own hints) and a non-`hint:` error entirely.
 *
 * THI-99 (two-tier near-empty signal) — `BotSources.tsx` uses the returned counts to decide
 * light vs. heavy presentation client-side via `isHeavyNearEmpty` below, rather than re-parsing
 * the sentence itself or duplicating the proportion rule. */
export function parseNearEmptyHint(error: string): NearEmptyHintCounts | null {
  if (!isHintMessage(error)) return null;
  const match = NEAR_EMPTY_HINT_PATTERN.exec(stripHintPrefix(error));
  if (!match) return null;
  return { count: Number(match[1]), total: Number(match[2]) };
}

/** Whether `error` is specifically `crawl_source`'s near-empty/rendered-mode nudge
 * (`renderedModeHint`) — as opposed to any of `DataSource.error`'s OTHER `hint:` shapes
 * (rate-limit/deadline truncation, off-site redirects, `verify_indexing`'s own hints). A thin
 * boolean wrapper over `parseNearEmptyHint` — kept as its own export because most callers
 * (e.g. deciding whether ANY deep link applies at all) don't need the counts, just the yes/no.
 * Same "one recognizer, not a hand-parsed guess" discipline as `isVerifyOwnedHint` above. */
export function isNearEmptyHint(error: string): boolean {
  return parseNearEmptyHint(error) !== null;
}

/** THI-99 (two-tier near-empty signal) — the SAME proportion rule `crawl_source`'s
 * `renderedModeHint` uses server-side to pick light vs. heavy copy (`nearEmptyCount * 2 >=
 * pagesEmitted`, i.e. at least half the emitted pages looked empty), exported so the dashboard
 * can classify tier client-side from `parseNearEmptyHint`'s counts WITHOUT re-deriving the "2x"
 * threshold from a second, independently-chosen number — the exact drift `NEAR_EMPTY_MIN_CHARS`'s
 * own doc comment warns about, just for a proportion instead of a char count. */
export function isHeavyNearEmpty(count: number, total: number): boolean {
  return count * 2 >= total;
}

export const SOURCE_FILE_LIMITS = {
  maxBytes: 4 * 1024 * 1024,
  allowedExtensions: ['pdf', 'docx', 'txt', 'md'] as const,
  allowedMime: [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    'text/markdown',
  ] as const,
};

/** The one place the extension-parsing rule for an uploaded source file is spelled out —
 * `name.includes('.') ? split('.').pop() : ''`, lowercased. Both `apps/api/src/routes
 * /sources.ts`'s 415 gate and `apps/dashboard/src/pages/BotSources.tsx`'s drop-zone gate call
 * this (via `isAllowedSourceFilename`, below) instead of each carrying their own copy of the
 * same edge case (no dot → empty string, which can never match an allowed extension) — the two
 * decisions used to be independently written and only happened to agree; now they're provably
 * one. Exported on its own, not just folded into `isAllowedSourceFilename`, because both call
 * sites also need the bare extension for their rejection message ("`.exe` isn't supported…"). */
export function sourceFileExtension(filename: string): string {
  return filename.includes('.') ? (filename.split('.').pop() ?? '').toLowerCase() : '';
}

/** Whether `filename`'s extension is one `SOURCE_FILE_LIMITS.allowedExtensions` lists — the
 * shared decision behind both the API's 415 and the dashboard's in-place rejection. */
export function isAllowedSourceFilename(filename: string): boolean {
  return (SOURCE_FILE_LIMITS.allowedExtensions as readonly string[]).includes(
    sourceFileExtension(filename),
  );
}
